import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Grid,
    Alert,
    Snackbar,
    CircularProgress,
    Divider,
    FormControlLabel,
    Switch,
    MenuItem,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import { useApi } from '../hooks/useApi';
import { useTranslation } from 'react-i18next';

const SettingsPage = () => {
    const { t } = useTranslation();
    const { get, post, put } = useApi();
    const [settings, setSettings] = useState({});
    const [originalSettings, setOriginalSettings] = useState({}); // Track originele waarden
    const [changedParameters, setChangedParameters] = useState(new Set()); // Track gewijzigde parameters
    
    // Nieuwe state voor display instellingen
    const [electricityDisplaySettings, setElectricityDisplaySettings] = useState({
        show_all_in: true
    });
    const [originalElectricityDisplaySettings, setOriginalElectricityDisplaySettings] = useState({});
    const [hydrogenDisplaySettings, setHydrogenDisplaySettings] = useState({
        hydrogen_type: 'green',
        show_retail: true
    });
    const [originalHydrogenDisplaySettings, setOriginalHydrogenDisplaySettings] = useState({});
    
    const [loading, setLoading] = useState(true);
    const [sendingTestEmail, setSendingTestEmail] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [testEmailSuccess, setTestEmailSuccess] = useState(null);
    const [testEmailError, setTestEmailError] = useState(null);

    // Haal de instellingen op bij het laden van de pagina
    useEffect(() => {
        fetchAllSettings();
    }, []);

    const fetchAllSettings = async () => {
        try {
            setLoading(true);
            
            // Haal algemene configuratie op
            await fetchSettings();
            
            // Haal electricity display settings op
            await fetchElectricityDisplaySettings();
            
            // Haal hydrogen display settings op
            await fetchHydrogenDisplaySettings();
            
        } catch (error) {
            setError('Kon instellingen niet ophalen');
            console.error('Error fetching all settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSettings = async () => {
        try {
            const response = await get('/config');
            if (response.data && response.data.configuration) {
                const settingsObject = {};
                response.data.configuration.forEach(setting => {
                    settingsObject[setting.parameter] = setting.value;
                });
                setSettings(settingsObject);
                setOriginalSettings(settingsObject); // Sla originele waarden op
                setChangedParameters(new Set()); // Reset gewijzigde parameters
            }
        } catch (error) {
            setError(t('errors.fetchSettings'));
            console.error('Error fetching settings:', error);
        }
    };

    const fetchElectricityDisplaySettings = async () => {
        try {
            const response = await get('/electricity-price/settings');
            if (response.data) {
                setElectricityDisplaySettings(response.data);
                setOriginalElectricityDisplaySettings(response.data);
                console.log('Electricity display settings opgehaald:', response.data);
            }
        } catch (error) {
            console.error('Error fetching electricity display settings:', error);
            // Gebruik defaults
            setElectricityDisplaySettings({ show_all_in: true });
            setOriginalElectricityDisplaySettings({ show_all_in: true });
        }
    };

    const fetchHydrogenDisplaySettings = async () => {
        try {
            const response = await get('/hydrogen-price/display-settings');
            if (response.data) {
                setHydrogenDisplaySettings(response.data);
                setOriginalHydrogenDisplaySettings(response.data);
                console.log('Hydrogen display settings opgehaald:', response.data);
            }
        } catch (error) {
            console.error('Error fetching hydrogen display settings:', error);
            // Gebruik defaults
            setHydrogenDisplaySettings({ hydrogen_type: 'green', show_retail: true });
            setOriginalHydrogenDisplaySettings({ hydrogen_type: 'green', show_retail: true });
        }
    };

    const handleSettingChange = (parameter, value) => {
        setSettings(prev => ({
            ...prev,
            [parameter]: value
        }));
        
        // Check of de waarde is gewijzigd ten opzichte van de originele waarde
        const originalValue = originalSettings[parameter];
        
        // Voor nieuwe parameters (undefined in originalSettings) en lege string waarden
        // behandelen we een lege string als de default waarde
        const effectiveOriginalValue = originalValue !== undefined ? originalValue : '';
        const effectiveCurrentValue = value || '';
        
        if (effectiveOriginalValue !== effectiveCurrentValue) {
            setChangedParameters(prev => new Set([...prev, parameter]));
        } else {
            setChangedParameters(prev => {
                const newSet = new Set(prev);
                newSet.delete(parameter);
                return newSet;
            });
        }
    };

    const handleElectricityDisplaySettingChange = (key, value) => {
        setElectricityDisplaySettings(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleHydrogenDisplaySettingChange = (key, value) => {
        setHydrogenDisplaySettings(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const hasElectricityDisplayChanges = () => {
        return JSON.stringify(electricityDisplaySettings) !== JSON.stringify(originalElectricityDisplaySettings);
    };

    const hasHydrogenDisplayChanges = () => {
        return JSON.stringify(hydrogenDisplaySettings) !== JSON.stringify(originalHydrogenDisplaySettings);
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            
            // Sla algemene configuratie op
            const changedParams = Array.from(changedParameters);
            
            if (changedParams.length > 0) {
                console.log('Opslaan van gewijzigde parameters:', changedParams);

                for (const parameter of changedParams) {
                    const value = settings[parameter];
                    // Email, tekst en boolean parameters moeten als string worden opgeslagen
                    const isTextParameter = [
                        'email_recipient', 'email_subject', 'email_title', 'device_id',
                        'plot_cleanup_enabled', 'email_enabled'
                    ].includes(parameter);
                    await post('/config', {
                        parameter,
                        value: isTextParameter ? value : parseFloat(value)
                    });
                }

                // Update originele settings na succesvol opslaan
                setOriginalSettings(prev => {
                    const newOriginal = { ...prev };
                    changedParams.forEach(param => {
                        newOriginal[param] = settings[param];
                    });
                    return newOriginal;
                });
                
                // Reset gewijzigde parameters
                setChangedParameters(new Set());
            }

            // Sla electricity display settings op
            if (hasElectricityDisplayChanges()) {
                console.log('Opslaan electricity display settings:', electricityDisplaySettings);
                await put('/electricity-price/settings', electricityDisplaySettings);
                setOriginalElectricityDisplaySettings(electricityDisplaySettings);
            }

            // Sla hydrogen display settings op
            if (hasHydrogenDisplayChanges()) {
                console.log('Opslaan hydrogen display settings:', hydrogenDisplaySettings);
                await put('/hydrogen-price/display-settings', hydrogenDisplaySettings);
                setOriginalHydrogenDisplaySettings(hydrogenDisplaySettings);
            }
            
            // Check of er waterstof-gerelateerde parameters zijn gewijzigd
            const hydrogenParams = changedParams.filter(param => param.startsWith('h2_'));
            if (hydrogenParams.length > 0) {
                try {
                    await post('/hydrogen-price/store-price-data', {
                        hours_forward: 48 // Sla komende 48 uur op
                    });
                    console.log('Waterstof prijzen opgeslagen in database vanaf nu vooruit na settings wijzigingen');
                } catch (storeError) {
                    console.warn('Kon waterstof prijzen niet opslaan in database:', storeError);
                }
            }
            
            // Check of er stroomprijs-gerelateerde parameters zijn gewijzigd
            const electricityParams = changedParams.filter(param => param.startsWith('elec_'));
            if (electricityParams.length > 0 || hasElectricityDisplayChanges()) {
                try {
                    await post('/electricity-price/store-price-data', {
                        hours_forward: 48 // Sla komende 48 uur op
                    });
                    console.log('Stroomprijs componenten opgeslagen in database vanaf nu vooruit na settings wijzigingen');
                } catch (storeError) {
                    console.warn('Kon stroomprijs componenten niet opslaan in database:', storeError);
                }
            }
            
            setSuccess(true);
        } catch (error) {
            setError(t('errors.saveSettings'));
            console.error('Error saving settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSendTestEmail = async () => {
        try {
            setSendingTestEmail(true);
            setTestEmailError(null);
            setTestEmailSuccess(null);
            
            const response = await post('/config/send-test-email');
            
            if (response.data && response.data.success) {
                setTestEmailSuccess(response.data.message);
                console.log('Test email verstuurd:', response.data);
            } else {
                setTestEmailError('Test email kon niet worden verstuurd');
            }
        } catch (error) {
            console.error('Fout bij versturen test email:', error);
            
            // Probeer specifieke foutmelding uit response te halen
            let errorMessage = 'Er is een fout opgetreden bij het versturen van de test email';
            
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            } else if (error.response?.status === 404) {
                errorMessage = 'Geen prijsdata beschikbaar voor vandaag. Test email kan niet worden verstuurd.';
            } else if (error.response?.status === 500) {
                errorMessage = 'Server fout bij versturen test email. Controleer de instellingen.';
            }
            
            setTestEmailError(errorMessage);
        } finally {
            setSendingTestEmail(false);
        }
    };

    // Check of er wijzigingen zijn
    const hasChanges = changedParameters.size > 0 || hasElectricityDisplayChanges() || hasHydrogenDisplayChanges();

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                {t('settings.title')}
            </Typography>
            
            {/* Prijs Planning Instellingen */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Prijs Planning Instellingen
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={t('settings.ewmaSpan')}
                                type="number"
                                value={settings.ewma_span || ''}
                                onChange={(e) => handleSettingChange('ewma_span', e.target.value)}
                                margin="normal"
                                helperText="Aantal periodes voor EWMA berekening"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={t('settings.minRunTime')}
                                type="number"
                                value={settings.min_run_time || ''}
                                onChange={(e) => handleSettingChange('min_run_time', e.target.value)}
                                margin="normal"
                                helperText="Minimale draaitijd in minuten"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={t('settings.windowSize')}
                                type="number"
                                value={settings.window_size || ''}
                                onChange={(e) => handleSettingChange('window_size', e.target.value)}
                                margin="normal"
                                helperText="Aantal periodes voor gemiddelde berekening"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={t('settings.priceThreshold')}
                                type="number"
                                step="0.01"
                                value={settings.price_threshold || ''}
                                onChange={(e) => handleSettingChange('price_threshold', e.target.value)}
                                margin="normal"
                                helperText="Maximale prijs voor inschakeling (€/kWh)"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={t('settings.maxPrice')}
                                type="number"
                                step="0.01"
                                value={settings.max_price || ''}
                                onChange={(e) => handleSettingChange('max_price', e.target.value)}
                                margin="normal"
                                helperText="Absolute maximale prijs (€/kWh)"
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Elektrolyzer Apparaat Instellingen */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Elektrolyzer Apparaat Instellingen
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={t('settings.deviceId')}
                                type="text"
                                value={settings.device_id || ''}
                                onChange={(e) => handleSettingChange('device_id', e.target.value)}
                                margin="normal"
                                helperText="Unieke identifier van het apparaat"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={t('settings.power')}
                                type="number"
                                step="0.1"
                                value={settings.power || ''}
                                onChange={(e) => handleSettingChange('power', e.target.value)}
                                margin="normal"
                                helperText="Elektrisch vermogen in kW"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={t('settings.efficiency')}
                                type="number"
                                step="0.1"
                                value={settings.efficiency || ''}
                                onChange={(e) => handleSettingChange('efficiency', e.target.value)}
                                margin="normal"
                                helperText="Efficiëntie in procenten (%)"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={t('settings.energyPerKgKwh')}
                                type="number"
                                step="0.1"
                                value={settings.energy_per_kg_kwh || ''}
                                onChange={(e) => handleSettingChange('energy_per_kg_kwh', e.target.value)}
                                margin="normal"
                                helperText="Energie per kg waterstof (kWh/kg)"
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Waterstof Prijsberekening Instellingen */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Waterstof Prijsberekening Instellingen
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Forecast Uren"
                                type="number"
                                value={settings.h2_forecast_hours || ''}
                                onChange={(e) => handleSettingChange('h2_forecast_hours', e.target.value)}
                                margin="normal"
                                helperText="Aantal uren vooruit te voorspellen"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="TTF Coëfficiënt (a)"
                                type="number"
                                step="0.01"
                                value={settings.h2_a || ''}
                                onChange={(e) => handleSettingChange('h2_a', e.target.value)}
                                margin="normal"
                                helperText="TTF prijs multiplicator"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Basisprijs (b)"
                                type="number"
                                step="0.1"
                                value={settings.h2_b || ''}
                                onChange={(e) => handleSettingChange('h2_b', e.target.value)}
                                margin="normal"
                                helperText="Vaste basisprijs (€/kg)"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Compressie (€/kg)"
                                type="number"
                                step="0.1"
                                value={settings.h2_compressie || ''}
                                onChange={(e) => handleSettingChange('h2_compressie', e.target.value)}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Opslag (€/kg)"
                                type="number"
                                step="0.1"
                                value={settings.h2_opslag || ''}
                                onChange={(e) => handleSettingChange('h2_opslag', e.target.value)}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Transport (€/kg)"
                                type="number"
                                step="0.1"
                                value={settings.h2_transport || ''}
                                onChange={(e) => handleSettingChange('h2_transport', e.target.value)}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Infrastructuur (€/kg)"
                                type="number"
                                step="0.1"
                                value={settings.h2_infra || ''}
                                onChange={(e) => handleSettingChange('h2_infra', e.target.value)}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Marge (€/kg)"
                                type="number"
                                step="0.1"
                                value={settings.h2_marge || ''}
                                onChange={(e) => handleSettingChange('h2_marge', e.target.value)}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Subsidie Groen (€/kg)"
                                type="number"
                                step="0.1"
                                value={settings.h2_subsidie || ''}
                                onChange={(e) => handleSettingChange('h2_subsidie', e.target.value)}
                                margin="normal"
                                helperText="Subsidie voor groene waterstof"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Premium Groen (€/kg)"
                                type="number"
                                step="0.1"
                                value={settings.h2_premium || ''}
                                onChange={(e) => handleSettingChange('h2_premium', e.target.value)}
                                margin="normal"
                                helperText="Premium voor groene waterstof"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="CCS Kosten Blauw (€/kg)"
                                type="number"
                                step="0.1"
                                value={settings.h2_ccs_kosten || ''}
                                onChange={(e) => handleSettingChange('h2_ccs_kosten', e.target.value)}
                                margin="normal"
                                helperText="CO₂ opslag kosten voor blauwe waterstof"
                            />
                        </Grid>
                        
                        {/* Waterstof Display Instellingen */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                                Display Instellingen
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Deze instellingen bepalen wat standaard wordt getoond in planning simulatie en overzichten.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Standaard Waterstof Type"
                                value={hydrogenDisplaySettings.hydrogen_type || 'green'}
                                onChange={(e) => handleHydrogenDisplaySettingChange('hydrogen_type', e.target.value)}
                                margin="normal"
                                helperText="Welk waterstof type standaard getoond wordt in planning en overzichten"
                            >
                                <MenuItem value="green">Groen</MenuItem>
                                <MenuItem value="grey">Grijs</MenuItem>
                                <MenuItem value="blue">Blauw</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={hydrogenDisplaySettings.show_retail || false}
                                        onChange={(e) => handleHydrogenDisplaySettingChange('show_retail', e.target.checked)}
                                    />
                                }
                                label="Standaard Retail Prijzen Tonen"
                                sx={{ mt: 2 }}
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Of retail prijzen (inclusief marge en ketenkosten) of wholesale (markt) prijzen getoond worden in planning en overzichten
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Stroomprijs Prijsberekening Instellingen */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Stroomprijs Prijsberekening Instellingen
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Netbeheerder Tarief (€/kWh)"
                                type="number"
                                step="0.001"
                                value={settings.elec_netbeheerder || ''}
                                onChange={(e) => handleSettingChange('elec_netbeheerder', e.target.value)}
                                margin="normal"
                                helperText="Tarief voor netbeheer en transport"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Leverancier Tarief (€/kWh)"
                                type="number"
                                step="0.001"
                                value={settings.elec_leverancier || ''}
                                onChange={(e) => handleSettingChange('elec_leverancier', e.target.value)}
                                margin="normal"
                                helperText="Marge en kosten van energieleverancier"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={electricityDisplaySettings.show_all_in || false}
                                        onChange={(e) => handleElectricityDisplaySettingChange('show_all_in', e.target.checked)}
                                    />
                                }
                                label="Toon All-in Prijs in Grafieken"
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Of all-in prijzen (inclusief netbeheerder + leverancier) getoond worden in planning en overzichten
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Systeem Instellingen */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Systeem Instellingen
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Plot Bewaarperiode (dagen)"
                                type="number"
                                value={settings.plot_retention_days || ''}
                                onChange={(e) => handleSettingChange('plot_retention_days', e.target.value)}
                                margin="normal"
                                helperText="Aantal dagen om plots te bewaren voordat ze worden verwijderd"
                                inputProps={{ min: 1, max: 365 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Polling Interval (minuten)"
                                type="number"
                                value={settings.polling_interval_minutes || ''}
                                onChange={(e) => handleSettingChange('polling_interval_minutes', e.target.value)}
                                margin="normal"
                                helperText="Hoe vaak het systeem controleert op nieuwe data"
                                inputProps={{ min: 1, max: 60 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.plot_cleanup_enabled === 'true' || settings.plot_cleanup_enabled === true}
                                        onChange={(e) => handleSettingChange('plot_cleanup_enabled', e.target.checked ? 'true' : 'false')}
                                    />
                                }
                                label="Automatische Plot Cleanup"
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Verwijder automatisch oude plots ouder dan de bewaarperiode
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.email_enabled === 'true' || settings.email_enabled === true}
                                        onChange={(e) => handleSettingChange('email_enabled', e.target.checked ? 'true' : 'false')}
                                    />
                                }
                                label="Email Rapporten Ingeschakeld"
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Schakel automatische email verzending in of uit
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Email Instellingen */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Email Instellingen
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Email Ontvangers"
                                type="email"
                                value={settings.email_recipient || ''}
                                onChange={(e) => handleSettingChange('email_recipient', e.target.value)}
                                margin="normal"
                                error={settings.email_recipient && !settings.email_recipient.split(',').every(email => email.trim().match(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i))}
                                helperText={settings.email_recipient && !settings.email_recipient.split(',').every(email => email.trim().match(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i)) ? 'Ongeldige email adressen gevonden' : 'Email adressen voor rapporten (meerdere gescheiden door komma)'}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Email Onderwerp"
                                type="text"
                                value={settings.email_subject || ''}
                                onChange={(e) => handleSettingChange('email_subject', e.target.value)}
                                margin="normal"
                                helperText="Onderwerpregel voor email rapporten"
                                placeholder="Holthausen Elektrolyzer Rapport"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Email Titel"
                                type="text"
                                value={settings.email_title || ''}
                                onChange={(e) => handleSettingChange('email_title', e.target.value)}
                                margin="normal"
                                helperText="Hoofdtitel in de email"
                                placeholder="Holthausen Elektrolyzer Planning Rapport"
                            />
                        </Grid>
                    </Grid>
                    
                    {/* Test Email Sectie */}
                    <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
                        <Typography variant="h6" gutterBottom>
                            Planning Email Test
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Verstuur een test email met de uitgebreide planning overzicht voor vandaag. 
                            Deze email bevat dezelfde informatie als automatisch wordt verstuurd, 
                            inclusief planning grafiek, handmatige overrides, en alle statistieken.
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                            <Button
                                variant="outlined"
                                size="large"
                                startIcon={<EmailIcon />}
                                onClick={handleSendTestEmail}
                                disabled={sendingTestEmail || !settings.email_recipient}
                                sx={{ px: 4, py: 1.5 }}
                            >
                                {sendingTestEmail ? 'Versturen...' : 'Test Email Versturen'}
                            </Button>
                            
                            {!settings.email_recipient && (
                                <Typography variant="body2" color="warning.main">
                                    Configureer eerst een email adres hierboven.
                                </Typography>
                            )}
                            
                            {sendingTestEmail && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" color="text.secondary">
                                        Planning wordt gegenereerd en email wordt verstuurd...
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Algemene Opslaan Knop */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <Button
                    variant="contained"
                    size="large"
                    color={hasChanges ? "secondary" : "primary"}
                    onClick={handleSave}
                    disabled={loading || !hasChanges}
                    sx={{ px: 4, py: 1.5 }}
                >
                    {loading ? t('common.saving') : hasChanges ? t('settings.save') : t('common.saved')}
                </Button>
            </Box>



            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            </Snackbar>

            <Snackbar
                open={success}
                autoHideDuration={6000}
                onClose={() => setSuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setSuccess(false)}>
                    {t('settings.saveSuccess')}
                </Alert>
            </Snackbar>

            {/* Test Email Success */}
            <Snackbar
                open={!!testEmailSuccess}
                autoHideDuration={8000}
                onClose={() => setTestEmailSuccess(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setTestEmailSuccess(null)}>
                    {testEmailSuccess}
                </Alert>
            </Snackbar>

            {/* Test Email Error */}
            <Snackbar
                open={!!testEmailError}
                autoHideDuration={10000}
                onClose={() => setTestEmailError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="error" onClose={() => setTestEmailError(null)}>
                    {testEmailError}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SettingsPage; 